import { useState, useEffect } from "react";
import { Sparkles, Lightbulb, BookOpen, ArrowRight, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAITutor, type QuizRemediationData } from "@/hooks/useAITutor";
import { supabase } from "@/integrations/supabase/client";
import AdaptiveMediaIntervention from "@/components/ai/AdaptiveMediaIntervention";


// Static fallback content
const FALLBACK: QuizRemediationData = {
  title: "Let's Review Together",
  explanation: "Some of the concepts in this lesson need a bit more practice. That's completely normal — every expert started as a beginner.",
  misconception: "Financial concepts often have subtle differences that are easy to mix up at first.",
  example: "Think of it like learning to ride a bike — each attempt gets smoother.",
  hint: "Re-read the lesson material and pay close attention to the key definitions before trying again.",
  recommended_next_step: "review_lesson",
  recommended_review_topic: "the core lesson concepts",
};

interface Props {
  lessonId?: string;
  lessonName: string;
  courseName: string;
  score: number;
  totalQuestions: number;
  wrongAnswers?: { question: string; selectedAnswer: string; correctAnswer: string }[];
  onDismiss?: () => void;
  onReviewLesson?: () => void;
  onAskAI?: () => void;
}

export default function AIRemediationCard({
  lessonId,
  lessonName,
  courseName,
  score,
  totalQuestions,
  wrongAnswers,
  onDismiss,
  onReviewLesson,
  onAskAI,
}: Props) {
  const { loading, getQuizRemediation } = useAITutor();
  const [data, setData] = useState<QuizRemediationData | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRemediation = async () => {
      const result = await getQuizRemediation({
        lessonName,
        courseName,
        score,
        totalQuestions,
        wrongAnswers,
      });
      if (cancelled) return;
      if (result.fallback || !result.data) {
        setUseFallback(true);
      } else {
        setData(result.data);
      }
    };
    fetchRemediation();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  const content = data || (useFallback ? FALLBACK : null);

  /* Intervention telemetry: record the delivery (and the modality actually used,
     including the soft-fail text fallback) so effectiveness can be measured
     against the learner's next independent attempt. */
  const [deliveryLogged, setDeliveryLogged] = useState(false);
  useEffect(() => {
    if (!content || deliveryLogged) return;
    setDeliveryLogged(true);
    void (supabase as never as { rpc: (n: string, a: unknown) => Promise<unknown> }).rpc(
      "record_intervention_delivery",
      {
        p_intervention_type: "quiz_remediation",
        p_modality: useFallback ? "static_text_fallback" : "ai_study_coach_text",
        p_lesson_id: lessonId ?? null,
        p_module_key: null,
        p_competency_key: "lesson_quiz",
        p_delivered: true,
        p_metadata: { score, total_questions: totalQuestions, course_name: courseName },
      },
    );
  }, [content, deliveryLogged, useFallback, lessonId, score, totalQuestions, courseName]);

  return (
    <div className="rounded-2xl border border-primary/20 bg-card/90 backdrop-blur-md p-5 space-y-4 shadow-[0_0_30px_hsl(var(--primary)/0.1)]">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-sm text-foreground">AI Study Coach</h3>
          <p className="text-[10px] text-muted-foreground">Personalized guidance for you</p>
        </div>
      </div>

      {loading && !content ? (
        <div className="flex items-center justify-center py-8 gap-2">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Analyzing your answers...</span>
        </div>
      ) : content ? (
        <>
          <div className="space-y-3">
            <h4 className="font-heading font-semibold text-foreground text-sm">{content.title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{content.explanation}</p>
          </div>

          {/* Misconception */}
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-semibold text-amber-400">Common Mix-up</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.misconception}</p>
          </div>

          {/* Example */}
          <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary">Real-World Example</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.example}</p>
          </div>

          {/* Hint */}
          <div className="bg-accent/5 border border-accent/15 rounded-xl p-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-accent">💡 Hint: </span>
              {content.hint}
            </p>
          </div>

          {/* The Adaptive Learning Engine may also resolve a grounded visual or
              short explanation for the competency it is currently working on.
              It renders nothing when the engine chose another modality. */}
          <AdaptiveMediaIntervention enabled={Boolean(content)} />



          {/* Recommendation */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowRight className="w-3 h-3 text-primary" />
            <span>
              We recommend: <strong className="text-foreground">
                {content.recommended_next_step === "retry_now" ? "Retry the quiz now" : "Review the lesson first"}
              </strong>
              {content.recommended_review_topic && (
                <> — focus on <em>{content.recommended_review_topic}</em></>
              )}
            </span>
          </div>
          {/* Action buttons */}
          {(onReviewLesson || onAskAI) && (
            <div className="flex gap-2 pt-1">
              {onReviewLesson && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReviewLesson}
                  className="flex-1 text-xs gap-1.5 border-border text-muted-foreground hover:bg-muted/30"
                >
                  <BookOpen className="w-3 h-3" />
                  Review Lesson
                </Button>
              )}
              {onAskAI && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAskAI}
                  className="flex-1 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
                >
                  <Sparkles className="w-3 h-3" />
                  Ask AI
                </Button>
              )}
            </div>
          )}
        </>
      ) : null}

      {onDismiss && (
        <Button variant="ghost" size="sm" onClick={onDismiss} className="w-full text-xs text-muted-foreground">
          Dismiss
        </Button>
      )}
    </div>
  );
}
